import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Check, ExternalLink, Grid3x3, List } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Deck } from "@/lib/classes/deck";
import { DeckPreview } from "./DeckPreview";

interface CompletionScreenProps {
  deckCode: string;
  deck: Deck | null;
}

export function CompletionScreen({ deckCode, deck }: CompletionScreenProps) {
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deckCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy deck code:", err);
    }
  };

  const handleExportToBuilder = () => {
    navigate(`/deck-builder?code=${encodeURIComponent(deckCode)}`);
  };

  const handleStartNewDraft = () => {
    // Clear localStorage to ensure a fresh start
    localStorage.removeItem("deckDraftState");
    // Small delay to ensure localStorage is cleared, then reload
    setTimeout(() => {
      window.location.href = "/deck-drafter";
    }, 100);
  };

  // Show preview mode if deck exists and preview is enabled
  if (showPreview && deck) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <div className="container mx-auto px-4 pb-2 max-w-7xl flex-shrink-0">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Deck Preview</h1>
            <Button
              onClick={() => setShowPreview(false)}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <List className="h-4 w-4" />
              Back to Summary
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="container mx-auto px-4 pb-4 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <DeckPreview deck={deck} />
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl text-center">Draft Complete!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {deck ? (
              <div className="text-center">
                <h3 className="text-2xl font-semibold mb-2">
                  {deck.division.name}
                </h3>
                <p className="text-muted-foreground">
                  {deck.units.length} units • {deck.totalSpentActivationPoints} / {deck.division.maxActivationPoints} activation points
                </p>
                {deck.units.length === 0 && deckCode && (
                  <p className="text-sm text-yellow-500 mt-2">
                    Warning: No units found in deck. The deck code may be invalid or empty.
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center">
                <p className="text-muted-foreground">
                  Unable to load deck information. The deck code may be invalid.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                Deck Code
              </label>
              <div className="flex gap-2">
                <div className="flex-1 p-4 bg-muted rounded-lg border font-mono text-sm break-all">
                  {deckCode}
                </div>
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="icon"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {copied && (
                <p className="text-sm text-muted-foreground mt-2">Copied to clipboard!</p>
              )}
            </div>

            <div className="flex gap-4 justify-center flex-wrap">
              {deck && (
                <Button
                  onClick={() => setShowPreview(true)}
                  variant="outline"
                  size="lg"
                  className="gap-2"
                >
                  <Grid3x3 className="h-4 w-4" />
                  View Pack Preview
                </Button>
              )}
              <Button
                onClick={handleExportToBuilder}
                size="lg"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Deck Builder
              </Button>
              <Button
                onClick={handleStartNewDraft}
                variant="outline"
                size="lg"
              >
                Start New Draft
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
